import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Tab, { TabPresentationProps, TabProps } from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";

import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsInformationTabsStyles {
    container?: GridContainerProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    descriptionStyles?: ProductDescriptionStyles;
    tabGroupStyles?: TabGroupPresentationProps;
    tabStyles?: TabPresentationProps;
}

export const specificationsStyles: ProductDetailsInformationTabsStyles = {
    documentGridItem: {
        width: [12, 12, 12, 12, 12],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
        css: css`
            margin-right: 8px;
        `,
    },
    attributeTypeWrapper: {
        css: css`
            display: flex;
            height: 28px;
            padding-right: 25px;
        `,
    },
    tabGroupStyles: {
        cssOverrides: {
            wrapper: css`
                border: none;
                padding: 0;
            `,
            tabContent: css`
                margin-top: -2px;
                border: 2px solid ${getColor("common.border")};
            `,
            tabGroup: css`
                display: flex;
                flex-wrap: wrap;
                padding: 0 !important;
            `,
        },
    },
    tabStyles: {
        css: css`
            padding: 15px 20px;
            font-size: 18px;
            flex: 0 0 16.667%;
            cursor: pointer;
            outline: none !important;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        flex: 0 0 50%;
                    `,
                    css`
                        flex: 0 0 50%;
                    `,
                    css`
                        flex: 0 0 50%;
                    `,
                    css``,
                    css``,
                ]);
            }}
        `,
    },
};

const styles = specificationsStyles;

const ProductDetailsInformationTabs: React.FC<Props> = ({
    product: { brand, attributeTypes, documents, specifications, content, productTitle },
}) => {
    const renderingDocuments = documents && documents.length > 0;
    const renderingAttributes = attributeTypes && attributeTypes?.length > 0;
    const specificationsWithoutProductBadge = specifications
        ?.filter(specification => specification.name !== "Product Badging")
        ?.filter(specification => !specification.name.startsWith("Badge_"));
    const renderingSpecifications = specificationsWithoutProductBadge && specificationsWithoutProductBadge.length > 0;

    const tabs = [];

    const attributeWrapperStyles = {
        css: css`
            display: flex;
            flex-direction: column;
            flex-wrap: wrap;
            width: fit-content;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        height: auto;
                    `,
                    css`
                        height: auto;
                    `,
                    css`
                        height: ${attributeTypes && attributeTypes?.length >= 15 ? "420" : "auto"};
                    `,
                    css`
                        height: ${attributeTypes && attributeTypes?.length >= 10 ? "280px" : "auto"};
                    `,
                    css`
                        height: ${attributeTypes && attributeTypes?.length >= 10 ? "280px" : "auto"};
                    `,
                ]);
            }}
        `,
    };

    if (renderingSpecifications) {
        const tabSpecifications = (
            <Tab headline="Features" tabKey="Features" {...styles.tabStyles}>
                {specifications
                    ?.filter(specification => !specification.name.startsWith("Badge_"))
                    ?.map((spec, index) => (
                        <StyledWrapper key={index}>
                            <StyledWrapper>{parse(spec.htmlContent, parserOptions)}</StyledWrapper>
                        </StyledWrapper>
                    ))}
            </Tab>
        );
        tabs.push(tabSpecifications);
    }

    if (renderingAttributes) {
        const attributes = (
            <Tab headline="Specifications" tabKey="Specifications" {...styles.tabStyles}>
                <StyledWrapper {...attributeWrapperStyles}>
                    {attributeTypes?.map((attributeType, index) => (
                        <StyledWrapper {...styles.attributeTypeWrapper} key={index}>
                            <StyledWrapper>
                                <Typography
                                    data-test-selector="attributes_item_label"
                                    {...styles.attributeTypeLabelText}
                                >
                                    {attributeType.label || attributeType.name}:
                                </Typography>
                            </StyledWrapper>
                            <StyledWrapper>
                                <span data-test-selector="attributes_item_value">
                                    {(attributeType.attributeValues ?? [])
                                        .map(attributeValue => attributeValue.valueDisplay)
                                        .join(", ")}
                                </span>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </Tab>
        );
        tabs.push(attributes);
    }

    if (renderingDocuments) {
        const tabDocuments = (
            <Tab headline="Documents" tabKey="Documents" {...styles.tabStyles}>
                <GridContainer {...styles.container}>
                    {documents?.map(document => (
                        <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                            <Link
                                href={document.filePath}
                                target="_new"
                                {...styles.documentLink}
                                data-test-selector={`productDetails_${document.id}`}
                            >
                                <IconMemo {...styles.documentIcon} />
                                <Typography {...styles.documentNameText}>{document.name}</Typography>
                            </Link>
                        </GridItem>
                    ))}
                </GridContainer>
            </Tab>
        );
        tabs.push(tabDocuments);
    }

    return (
        <TabGroup {...styles.tabGroupStyles} current="Features">
            {tabs}
        </TabGroup>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsInformationTabs),
    definition: {
        displayName: "Information Tabs",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
        fieldDefinitions: [],
    },
};

export default widgetModule;
